import * as React from 'react';
import { TextEdit } from '../../../text-edit';
import { FieldProperty, FieldProps } from './field.types';
import { useFieldController } from './field.controller';
import { TextField } from '../../../text-field';
import { I18n } from 'react-redux-i18n';
import { Title } from '../../../title';
import { BackBtn } from '../../../back-btn';
import { Switch } from '../../../switch';
import { Select } from '../../../select';
import { Fields } from '../../../fields';
import { FieldLayout } from '../../../field';
import { Subtitle } from '../../../subtitle';
import { DropdownOptions } from './components/dropdown-options';
import { QuestionnaireFieldListDto } from '@common/dto/questionnaire/field/list-dto';
import { RadioGroup, RadioGroupValue } from '../../../radio-group';
import { SkipLogic } from '@common/components/skip-logic';
import { MODULE_NAME } from '@modules/fhq-design-tool/constants/shared';
import { SkipLogicPredefinedControlType, SkipLogicProps } from '@common/components/skip-logic/skip-logic.types';
import { QuestionnaireFieldType } from '@common/dto/questionnaire/field/type';
import { Flex } from '@common/components/Flex';
import { ErrorText } from '@common/components/error-text';

import './field.styles.scss';

export const Field: React.FC<FieldProps> = (props) => {
  const { data, onGoBack, getFieldsForSkipLogic, getFoldersForSkipLogic, scrollParentToTop, errors } = props;

  const {
    dropdownOptions,
    processedSubfields,
    propertyCanBeEdited,
    fieldTypeChooserConfig,
    fieldSkipLogicConditions,
    handleSkipLogicChange,
    handleSkipLogicConditionAdd,
    handleUseSkipLogicChange,
    editingSubfield,
    goBackFromTableFieldEdit,
    handleSubfieldChange,
    handleTableFieldsOrderChange,
    navigateOnTableFieldEdit,
    handleTableFieldShowHide,
    canDeleteTableField,
    handleTableAllowDuplicationsChange,
    handleFieldTypeChange,
    handleFieldNameChange,
    handleFieldDescriptionChange,
    handleFieldRequiredChange,
    handleOptionsChange,
  } = useFieldController(props);

  React.useEffect(() => {
    scrollParentToTop();
  }, [Boolean(editingSubfield)]);

  if (editingSubfield) {
    return (
      <Field 
        scrollParentToTop={scrollParentToTop}
        data={editingSubfield}
        onGoBack={goBackFromTableFieldEdit}
        onChange={handleSubfieldChange as FieldProps['onChange']}
        getFoldersForSkipLogic={getFoldersForSkipLogic}
        getFieldsForSkipLogic={getFieldsForSkipLogic}
      />
    );
  }

  const hasDropdownOption = fieldTypeChooserConfig?.options.some((x) => x.value === QuestionnaireFieldType.dropdown);

  return (
    <div className="FhqDesignToolQuestionnaireDesignerField" >
      <BackBtn className="FhqDesignToolQuestionnaireDesignerField__back-btn" onClick={onGoBack} />
      
      <TextEdit
        disabled={!propertyCanBeEdited(FieldProperty.name)}
        metadata={{
          description: 'name',
        }}
        value={data.name}
        onChange={handleFieldNameChange}
      />

      <Flex>
        {data.systemField && <Subtitle className="FhqDesignToolQuestionnaireDesignerField__system-field-chip" type="chip" text={I18n.t('System Field')} />}
        <Subtitle type="chip" text={(data as QuestionnaireFieldListDto).path} />
      </Flex>

      <TextField 
        disabled={!propertyCanBeEdited()}
        metadata={{
          description: 'tooltip',
        }}
        labelWidth={100}
        description={I18n.t('Field Description')}
        label={I18n.t('Tooltip')}
        value={data.description || ''}
        onChange={handleFieldDescriptionChange}
      />

      <Switch 
        disabled={!propertyCanBeEdited(FieldProperty.required)}
        labelWidth={100}
        metadata={{
          description: 'required',
        }}  
        label={I18n.t('Required')}
        value={data.required ?? false}
        onChange={handleFieldRequiredChange}
      />

      <Title text={I18n.t('Skip Logic')} />
      <Subtitle 
        text={I18n.t('skipLogicDescriptionField')}
      />
      <RadioGroup
        disabled={!propertyCanBeEdited()}
        margin="12px 0 12px 0"
        value={`${data.skipLogicEnabled ?? false}`}
        options={[
          { id: "false", name: I18n.t("Show Always") },
          { id: "true", name: I18n.t("Show only if condition below is true"), },
        ]}
        onChange={handleUseSkipLogicChange as (v: RadioGroupValue) => void}
        metadata={{
          description: "field-use-skip-logic",
        }}
      />
      {data.skipLogicEnabled && (
        <SkipLogic
          disabled={!propertyCanBeEdited()}
          moduleName={MODULE_NAME}
          className="FhqDesignToolQuestionnaireDesignerField__skip-logic"
          conditions={fieldSkipLogicConditions}
          onRemove={handleSkipLogicChange}
          onChange={handleSkipLogicChange}
          onAdd={handleSkipLogicConditionAdd as SkipLogicProps['onAdd']}
          getFolders={getFoldersForSkipLogic}
          getFields={getFieldsForSkipLogic}
          controls={[
            { type: SkipLogicPredefinedControlType.moveUp, handler: handleSkipLogicChange },
            { type: SkipLogicPredefinedControlType.moveDown, handler: handleSkipLogicChange },
            { type: SkipLogicPredefinedControlType.add },
          ]}
        />
      )}

      {hasDropdownOption && <Title text={I18n.t('Dropdown Options')} />}

      {fieldTypeChooserConfig && (
        <Select 
          labelWidth={100}
          disabled={!propertyCanBeEdited()}
          options={fieldTypeChooserConfig.options}
          value={data.type}
          metadata={{
            description: 'text-type'
          }}
          label={fieldTypeChooserConfig.label}
          onChange={(v) => handleFieldTypeChange(v as QuestionnaireFieldType)}
        />
      )}

      {hasDropdownOption && (
        <>
          <Subtitle text={I18n.t('Dropdown Values Display and Mapping')} />
          {errors?.map((x) => (
            <ErrorText key={x.text} text={x.text} />
          ))}
          <DropdownOptions 
            disabled={!propertyCanBeEdited()}
            className="FhqDesignToolQuestionnaireDesignerField__dropdown-options"
            data={dropdownOptions}
            onChange={handleOptionsChange}
          />
        </>
      )}

      {Boolean((data as QuestionnaireFieldListDto).subfields?.length) && data.type === QuestionnaireFieldType.table && (
        <>
          <Title text={I18n.t('Table Options')} />
          <Switch 
            disabled={!propertyCanBeEdited()}
            metadata={{
              description: 'allow-duplications',
            }}
            margin="12px 0 12px 0"
            layout={FieldLayout.componentThenLabel}
            label={I18n.t('Allow duplications')}
            value={data.allowDuplications ?? true}
            onChange={handleTableAllowDuplicationsChange}
          />
          <Fields 
            disabled={!propertyCanBeEdited()}
            data={processedSubfields}
            canDeleteField={canDeleteTableField}
            onOrderChange={handleTableFieldsOrderChange}
            onEdit={navigateOnTableFieldEdit}
            onShowStatusChange={handleTableFieldShowHide}
          />
        </>
      )}
    </div>
  );
};